import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";

export default function Risk() {
  const [company, setCompany] = useState("");
  const [project, setProject] = useState("");
  const [site, setSite] = useState("");
  const [assessor, setAssessor] = useState("");
  const [reviewDate, setReviewDate] = useState("");

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const [notes, setNotes] = useState("");
  const [testMode, setTestMode] = useState(true);

  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setLoading(true);
    setError(null);
    setDraft(null);

    try {
      const res = await fetch("/api/risk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          notes,
          company,
          project,
          site,
          assessor,
          date: today,
          reviewDate,
          test: testMode,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as any)?.error || "Failed");

      setDraft((data as any).draft);
    } catch (e: any) {
      setError(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Turn markdown into readable plain text for PDF export
  const markdownToPlainText = (md: string) => {
    return md
      .replace(/```[\s\S]*?```/g, "") // strip fenced code blocks
      .replace(/^#{1,6}\s+/gm, "") // strip headings markers
      .replace(/\*\*(.*?)\*\*/g, "$1") // bold
      .replace(/\*(.*?)\*/g, "$1") // italics
      .replace(/`([^`]+)`/g, "$1") // inline code
      .replace(/^\s*[-*+]\s+/gm, "• ") // bullets
      .replace(/^\s*\d+\.\s+/gm, (m) => m) // keep numbered lists
      .replace(/^---$/gm, "") // hr
      .trim();
  };

  const downloadPDF = async () => {
    if (!draft) return;

    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "a4" });

    const margin = 48;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const usableW = pageW - margin * 2;

    const safeName =
      (project || "risk-assessment")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "") || "risk-assessment";

    let y = margin;

    const drawHeader = () => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(company || "Risk Assessment", margin, y);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      y += 18;

      const meta = [
        project && `Project: ${project}`,
        site && `Site: ${site}`,
        assessor && `Assessor: ${assessor}`,
        reviewDate && `Review: ${reviewDate}`,
        `Date: ${today}`,
      ]
        .filter(Boolean)
        .join("  •  ");

      doc.text(meta, margin, y);

      y += 14;
      doc.setDrawColor(180);
      doc.line(margin, y, pageW - margin, y);
      y += 22;

      doc.setFontSize(11);
    };

    const addFooter = () => {
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(
        "Generated by Lumia — Review before issue. Not legal advice.",
        margin,
        pageH - 28
      );
      doc.setTextColor(0);
    };

    drawHeader();

    const text = markdownToPlainText(draft);
    const lines = doc.splitTextToSize(text, usableW);

    for (const line of lines) {
      if (y > pageH - 60) {
        addFooter();
        doc.addPage();
        y = margin;
        drawHeader();
      }

      doc.text(line, margin, y);
      y += 16;
    }

    addFooter();
    doc.save(`${safeName}.pdf`);
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 16px" }}>
      <h1>Construction Risk Assessment</h1>

      <p style={{ opacity: 0.75 }}>
        Generate a structured UK construction risk assessment draft. Edit as
        required before issue. Not legal advice.
      </p>

      <div style={{ marginTop: 24, display: "grid", gap: 12 }}>
        <input
          placeholder="Company Name"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          style={{ padding: 10, borderRadius: 8 }}
        />

        <input
          placeholder="Project Title"
          value={project}
          onChange={(e) => setProject(e.target.value)}
          style={{ padding: 10, borderRadius: 8 }}
        />

        <input
          placeholder="Site Address"
          value={site}
          onChange={(e) => setSite(e.target.value)}
          style={{ padding: 10, borderRadius: 8 }}
        />

        <input
          placeholder="Assessor Name"
          value={assessor}
          onChange={(e) => setAssessor(e.target.value)}
          style={{ padding: 10, borderRadius: 8 }}
        />

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontSize: 12, opacity: 0.75 }}>Review date</div>
            <input
              type="date"
              value={reviewDate}
              onChange={(e) => setReviewDate(e.target.value)}
              style={{ padding: 10, borderRadius: 8 }}
            />
          </div>

          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontSize: 12, opacity: 0.75 }}>Date</div>
            <input
              type="date"
              value={today}
              readOnly
              style={{ padding: 10, borderRadius: 8, opacity: 0.8 }}
            />
          </div>
        </div>
      </div>

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Describe work activities, hazards, materials, equipment, number of workers..."
        style={{
          width: "100%",
          minHeight: 160,
          marginTop: 18,
          padding: 12,
          borderRadius: 8,
        }}
      />

      <div style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
        <button
          onClick={generate}
          disabled={loading || !notes.trim()}
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            border: "none",
            cursor: loading || !notes.trim() ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Generating..." : "Generate Draft"}
        </button>

        <button
          onClick={downloadPDF}
          disabled={!draft}
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            border: "none",
            cursor: !draft ? "not-allowed" : "pointer",
            opacity: !draft ? 0.6 : 1,
          }}
        >
          Download PDF
        </button>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            opacity: 0.85,
            marginLeft: 8,
          }}
        >
          <input
            type="checkbox"
            checked={testMode}
            onChange={(e) => setTestMode(e.target.checked)}
          />
          Test mode (limits paid calls)
        </label>
      </div>

      {error && <p style={{ color: "tomato", marginTop: 12 }}>{error}</p>}

      {draft && (
        <div
          style={{
            marginTop: 24,
            padding: 24,
            borderRadius: 12,
            background: "rgba(255,255,255,0.04)",
            lineHeight: 1.6,
          }}
        >
          <ReactMarkdown>{draft}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}
